import { useMemo } from 'react';

import { WithChildren } from '../../../core/types/with-children.js';
import { XYChartOverlayPerformanceContext } from '../context/XYChartOverlayPerformance.context.js';
import { useBuildQuadtree } from '../hooks/useBuildQuadtree.js';
import { useXYChartData } from '../hooks/useXYChartData.js';
import { useXYChartScales } from '../hooks/useXYChartScales.js';
import { buildQueryClosest } from '../utils/query-closest-quadtree.js';
import { buildQueryGetClosestPoint } from '../utils/query-get-closest-point.js';
import { buildQueryHovered } from '../utils/query-hovered.js';

export const XYChartOverlayPerformanceProvider = ({
  children,
}: WithChildren) => {
  const axisIdToScales = useXYChartScales();
  const { datapointMetadataMap, axisIdToScaleDomain, seriesToMetadata } =
    useXYChartData();
  const seriesIdToTree = useBuildQuadtree();

  const contextValue = useMemo(() => {
    const queryClosest = seriesIdToTree
      ? buildQueryClosest(
          seriesIdToTree,
          axisIdToScales,
          seriesToMetadata,
          axisIdToScaleDomain,
          datapointMetadataMap,
        )
      : buildQueryGetClosestPoint(
          axisIdToScales,
          seriesToMetadata,
          datapointMetadataMap,
          axisIdToScaleDomain,
        );
    const queryHovered = buildQueryHovered(
      axisIdToScales,
      datapointMetadataMap,
      seriesToMetadata,
      axisIdToScaleDomain,
    );
    return { queryClosest, queryHovered };
  }, [
    seriesIdToTree,
    axisIdToScales,
    seriesToMetadata,
    axisIdToScaleDomain,
    datapointMetadataMap,
  ]);

  return (
    <XYChartOverlayPerformanceContext.Provider value={contextValue}>
      {children}
    </XYChartOverlayPerformanceContext.Provider>
  );
};

XYChartOverlayPerformanceProvider.displayName =
  'XYChartOverlayPerformanceProvider';
